// AISection.js — a concrete demonstration of the actual differentiator:
// ask your live data questions in plain English and get a real, grounded
// answer back — no export to Excel, no VLOOKUPs, no stale copy in someone's
// Downloads folder to reconcile against the source system later.
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Send, Plug } from "lucide-react";

// Real product capabilities, not invented ones - each exchange below
// mirrors something PrismDQ's assistant can actually do today: look up
// live entities/issues, build and send a report, and reconcile counts
// across two different connected systems.
const EXCHANGES = [
  {
    q: "Which entities still have open data quality issues?",
    a: "Three: Customers has 12 open issues (8 are missing email addresses), Orders has 4 (mismatched currency codes), and Addresses has 2 duplicate postcodes. Want me to open Customers so you can fix them?",
  },
  {
    q: "Build a status deck for this week's steering call.",
    a: "Done — 5 slides covering entities, open issues, rules, and the last 10 runs. Here's the download link, or I can email it straight to the project sponsor for you.",
  },
  {
    q: "How many customers came from Oracle vs the SharePoint extract?",
    a: "1,842 rows from the Oracle connection, 306 from SharePoint — 2,148 total after dedupe. 14 rows didn't match on either side; want to see them?",
  },
];

const CYCLE_MS = 5200;

export default function AISection() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActive((a) => (a + 1) % EXCHANGES.length), CYCLE_MS);
    return () => clearInterval(id);
  }, []);

  const current = EXCHANGES[active];

  return (
    <section id="ai-agents" className="relative py-24">
      {/* Ambient backdrop */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(1200px_420px_at_50%_-10%,rgba(96,165,250,0.18),transparent)]" />
      </div>

      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50/60 px-3 py-1 text-xs font-medium text-blue-700 backdrop-blur-md">
            <Sparkles className="h-3.5 w-3.5" /> Ask your data. Expose it to your agents.
          </span>
          <h2 className="mt-5 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            Data that's ready for AI, not just for reports.
          </h2>
          <p className="mt-3 text-slate-600">
            PrismDQ's built-in assistant reads your live project data — entities, issues,
            rules, runs — and answers in plain English, no exporting to Excel, no VLOOKUPs, no
            copy sitting in someone's Downloads folder going stale by lunchtime. Want to go
            further? Expose that same clean, governed data to your own AI agents over MCP —
            an open, growing standard — so any agent that speaks it can query and analyse
            your data with the same access controls and audit trail as a human user.
          </p>
        </div>

        {/* Two ways in: in-app assistant vs. your own agents */}
        <div className="mx-auto mt-10 grid max-w-3xl gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-blue-200/70 bg-white/70 p-5 backdrop-blur-md">
            <div className="flex items-center gap-2">
              <span className="grid h-8 w-8 place-items-center rounded-xl bg-gradient-to-br from-blue-600 to-cyan-400 text-white shadow">
                <Sparkles className="h-4 w-4" />
              </span>
              <span className="text-sm font-semibold text-slate-800">In-app assistant</span>
            </div>
            <p className="mt-3 text-sm text-slate-600">
              Ask questions, get status reports, and (for admins) create or update staged
              data — right inside PrismDQ, fully audit-logged.
            </p>
          </div>
          <div className="rounded-2xl border border-blue-200/70 bg-white/70 p-5 backdrop-blur-md">
            <div className="flex items-center gap-2">
              <span className="grid h-8 w-8 place-items-center rounded-xl bg-gradient-to-br from-blue-600 to-cyan-400 text-white shadow">
                <Plug className="h-4 w-4" />
              </span>
              <span className="text-sm font-semibold text-slate-800">Your own AI agents</span>
            </div>
            <p className="mt-3 text-sm text-slate-600">
              Connect Claude Desktop, Claude Code, or another MCP-compatible agent with a
              personal access token — same live data, same permissions, outside the browser.
            </p>
          </div>
        </div>

        {/* Mock chat panel — a real example exchange, cycling every few seconds */}
        <div className="mx-auto mt-12 max-w-2xl">
          <div className="rounded-3xl border border-blue-200/70 bg-white/80 p-5 shadow-2xl backdrop-blur-xl sm:p-7">
            <div className="flex items-center gap-2 border-b border-slate-100 pb-4">
              <span className="grid h-8 w-8 place-items-center rounded-xl bg-gradient-to-br from-blue-600 to-cyan-400 text-white shadow">
                <Sparkles className="h-4 w-4" />
              </span>
              <span className="text-sm font-semibold text-slate-800">PrismDQ Assistant</span>
              <span className="ml-auto text-xs text-slate-400">live in every project</span>
            </div>

            <div className="min-h-[220px] pt-5">
              <AnimatePresence mode="wait">
                <motion.div
                  key={active}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.4 }}
                  className="space-y-4"
                >
                  <div className="ml-auto max-w-[85%] rounded-2xl rounded-tr-sm bg-gradient-to-br from-blue-600 to-blue-500 px-4 py-3 text-sm text-white shadow">
                    {current.q}
                  </div>
                  <div className="mr-auto max-w-[85%] rounded-2xl rounded-tl-sm bg-slate-100 px-4 py-3 text-sm leading-relaxed text-slate-800">
                    {current.a}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Faux input, purely visual - reinforces "this is a chat", not a real form */}
            <div className="mt-5 flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-2.5">
              <span className="flex-1 truncate text-sm text-slate-400">Ask about this project…</span>
              <Send className="h-4 w-4 text-slate-400" />
            </div>

            <div className="mt-3 flex justify-center gap-1.5">
              {EXCHANGES.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === active ? "w-6 bg-blue-600" : "w-1.5 bg-slate-200"
                  }`}
                />
              ))}
            </div>
          </div>

          <div className="mt-6 flex items-center justify-center gap-2 text-center text-sm text-slate-500">
            <Plug className="h-4 w-4 text-blue-500" />
            Built on Model Context Protocol (MCP) — no proprietary lock-in to reach your data.
          </div>
        </div>
      </div>
    </section>
  );
}
